import React, { useEffect, useMemo, useCallback, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import { BaseForm, BaseFormFieldConfig, BaseFormSectionConfig } from '../shared/FormComponents';
import FormSkeleton from '../shared/FormComponents/FormSkeleton';
import SSECreationProgress from '../shared/SSECreationProgress';
import { Collaborator } from '../shared/CollaboratorSelector';
import { FormSchema, FormField } from '../../utils/validation/form-schema';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, createTableFormTourSteps } from '../shared/FeatureTour';
import { DropdownOption } from '../shared/TailwindComponents/Dropdown';
import Badge from '../shared/TailwindComponents/Badge';
import Button from '../shared/TailwindComponents/Button';
import Input from '../shared/TailwindComponents/Input';
import Dropdown from '../shared/TailwindComponents/Dropdown';
import TemplatePickerModal from './TemplatePickerModal';
import {
  PiPlusLight,
  PiTrashLight,
  PiTableLight,
  PiColumnsLight,
} from 'react-icons/pi';
import {
  createBatchTableSSE,
  resetBatchTableCreation,
  fetchTemplates,
} from '../../store/actions/BatchTables/BatchTablesActions';
import { fetchOrgPeople } from '../../store/actions/OnboardingActions';
import { fetchLinkableProjects } from '../../store/actions/Projects/ProjectActions';
import { TableTemplate, CreateTablePayload, ColumnDataType } from '../../types/batchTables';

// ─── Column Editor (custom BaseForm field) ──────────────────────────────────

interface ColumnItem {
  name: string;
  query: string;
  data_type: ColumnDataType;
}

const dataTypeOptions: DropdownOption[] = [
  { value: 'text', label: 'Text' },
  { value: 'date', label: 'Date' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'currency', label: 'Currency' },
];

interface ColumnsEditorProps {
  id?: string;
  name?: string;
  label?: string;
  value: ColumnItem[];
  onChange: (value: ColumnItem[]) => void;
  disabled?: boolean;
  required?: boolean;
  readOnly?: boolean;
  templateName?: string;
  templateColumns?: ColumnItem[];
}

const ColumnsEditor: React.FC<ColumnsEditorProps> = ({
  value = [],
  onChange,
  disabled,
  readOnly,
  templateName,
  templateColumns: tmplCols,
}) => {
  const { t } = useTranslation();
  const lastTemplateRef = useRef<string | undefined>(undefined);

  // Auto-seed columns when a template is selected
  useEffect(() => {
    if (tmplCols && tmplCols.length > 0 && templateName !== lastTemplateRef.current) {
      lastTemplateRef.current = templateName;
      onChange(tmplCols);
    }
  }, [tmplCols, templateName]); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = value || [];

  const handleColumnChange = (index: number, field: keyof ColumnItem, val: string) => {
    const updated = [...columns];
    updated[index] = { ...updated[index], [field]: val };
    onChange(updated);
  };

  const addColumn = () => {
    onChange([...columns, { name: '', query: '', data_type: 'text' as ColumnDataType }]);
  };

  const removeColumn = (index: number) => {
    const updated = [...columns];
    updated.splice(index, 1);
    onChange(updated);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base text-gray-900 dark:text-white">
            {t('Columns')}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {readOnly && templateName
              ? t(`Loaded from template "${templateName}". You can customize these columns or add more.`, { name: templateName })
              : t('Define the columns (queries) that will be applied to each document.')}
          </p>
        </div>
      </div>

      {columns.length === 0 && (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
          <PiTableLight className="w-10 h-10 mx-auto text-gray-400 mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('No columns defined. Add your first column or select a template above.')}
          </p>
          <Button
            type="button"
            variant="neutral"
            size="small"
            onClick={addColumn}
            className="mt-3 mx-auto"
          >
            <PiPlusLight className="w-4 h-4 mr-1" />
            {t('Add Column')}
          </Button>
        </div>
      )}

      <div className="space-y-3">
        {columns.map((col, index) => (
          <div
            key={index}
            className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4 relative group"
          >
            {/* Remove button - top right */}
            {!disabled && (
              <button
                type="button"
                onClick={() => removeColumn(index)}
                className="absolute top-3 right-3 p-1.5 rounded-md text-gray-300 dark:text-gray-600 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition opacity-0 group-hover:opacity-100"
                title={t('Remove column')}
              >
                <PiTrashLight className="w-4 h-4" />
              </button>
            )}

            {/* Row 1: Column number + Name + Type */}
            <div className="flex items-start gap-3">
              <div className="flex items-center pt-2">
                <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 bg-gray-200/60 dark:bg-gray-700/50 w-6 h-6 rounded-full flex items-center justify-center">
                  {index + 1}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <Input
                  id={`col-name-${index}`}
                  type="text"
                  label={t('Column Name')}
                  placeholder={t('e.g., Effective Date')}
                  value={col.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleColumnChange(index, 'name', e.target.value)
                  }
                  disabled={disabled}
                />
              </div>
              <div className="flex-shrink-0">
                <Dropdown
                  id={`col-type-${index}`}
                  label={t('Type')}
                  options={dataTypeOptions}
                  value={col.data_type}
                  onChange={(val) =>
                    handleColumnChange(index, 'data_type', Array.isArray(val) ? val[0] : val)
                  }
                />
              </div>
            </div>

            {/* Row 2: Query (full width) */}
            <div className="mt-2 pl-9">
              <Input
                id={`col-query-${index}`}
                type="text"
                label={t('AI Query')}
                placeholder={t('e.g., What is the effective date of the agreement?')}
                value={col.query}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleColumnChange(index, 'query', e.target.value)
                }
                disabled={disabled}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Footer: add column + count */}
      {columns.length > 0 && (
        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Badge color="gray">{columns.length}</Badge>
            <span>{t('column' + (columns.length !== 1 ? 's' : '') + ' defined')}</span>
          </div>
          {!disabled && (
            <Button
              type="button"
              variant="neutral"
              size="small"
              onClick={addColumn}
            >
              <PiPlusLight className="w-4 h-4 mr-1" />
              {t('Add Column')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Template Selector (opens modal picker) ────────────────────────────────

interface TemplateSelectorProps {
  id?: string;
  name?: string;
  label?: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  templates: TableTemplate[];
  selectedTemplate: TableTemplate | null;
  onTemplateChange: (templateId: string) => void;
}

const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  value,
  onChange,
  disabled,
  templates,
  selectedTemplate,
  onTemplateChange,
}) => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);

  const handleSelect = (templateId: string) => {
    onChange(templateId);
    onTemplateChange(templateId);
  };

  return (
    <>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
          {t('Template')}
        </label>
        <button
          type="button"
          disabled={disabled}
          onClick={() => setShowModal(true)}
          className={`w-full text-left px-4 py-2.5 rounded-lg border transition-colors flex items-center justify-between group ${
            selectedTemplate
              ? 'border-primary-300 dark:border-primary-700 bg-primary-50/50 dark:bg-primary-900/10'
              : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-primary-300 dark:hover:border-primary-600'
          }`}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <PiColumnsLight className={`w-4 h-4 flex-shrink-0 ${
              selectedTemplate ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400'
            }`} />
            {selectedTemplate ? (
              <div className="min-w-0">
                <span className="text-sm font-medium text-gray-900 dark:text-white truncate block">
                  {selectedTemplate.name}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {selectedTemplate.column_count} {t('columns')}
                </span>
              </div>
            ) : (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {t('Browse templates...')}
              </span>
            )}
          </div>
          <span className="text-xs text-primary-600 dark:text-primary-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
            {selectedTemplate ? t('Change') : t('Browse')}
          </span>
        </button>
      </div>

      {showModal && (
        <TemplatePickerModal
          templates={templates}
          selectedTemplateId={value}
          onSelect={handleSelect}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
};

// ─── Main Form ───────────────────────────────────────────────────────────────

interface CreateTableFormProps {
  onClose: (newUuid?: string) => void;
  matterId?: number;
  matterUuid?: string;
}

const CreateTableForm: React.FC<CreateTableFormProps> = ({ onClose, matterId, matterUuid }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const templates = useSelector((state: RootState) => state.batchTables.templates);
  const { isConnecting, connected, steps: sseSteps, complete, ok, summary } = useSelector(
    (state: RootState) => state.batchTables.sseCreate
  );

  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [projectOptions, setProjectOptions] = useState<DropdownOption[]>([]);
  const userNavigatedAwayRef = useRef(false);
  const sseStartedAtRef = useRef<number | null>(null);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'create-table-form',
    steps: createTableFormTourSteps,
    readyToShow: !isLoadingData,
    autoStartDelay: 600,
  });

  // ─── Load data ──────────────────────────────────────────────────────────

  useEffect(() => {
    const loadData = async () => {
      setIsLoadingData(true);
      try {
        const [, , projectsResp] = await Promise.all([
          dispatch(fetchTemplates() as any),
          dispatch(fetchOrgPeople()),
          dispatch(fetchLinkableProjects() as any),
        ]);
        if (projectsResp && Array.isArray(projectsResp)) {
          setProjectOptions(projectsResp.map((p: any) => ({ value: p.uuid, label: p.title })));
        }
      } catch (error: any) {
        console.error('[CreateTableForm] Error loading data:', error?.message);
      } finally {
        setIsLoadingData(false);
      }
    };
    loadData();
  }, [dispatch]);

  // ─── Template handling ──────────────────────────────────────────────────

  const selectedTemplate = useMemo(() => {
    if (!selectedTemplateId) return null;
    return templates.find((t: TableTemplate) => String(t.id) === selectedTemplateId) || null;
  }, [templates, selectedTemplateId]);

  const templateColumns: ColumnItem[] = useMemo(() => {
    if (!selectedTemplate?.columns) return [];
    return selectedTemplate.columns.map((c) => ({
      name: c.name,
      query: c.query,
      data_type: c.data_type || 'text',
    }));
  }, [selectedTemplate]);

  // ─── Template change handler ─────────────────────────────────────────

  const handleTemplateChange = useCallback(
    (templateId: string) => {
      setSelectedTemplateId(templateId);
    },
    []
  );

  // ─── Allowed file extensions for tables ─────────────────────────────────

  const tableFileExtensions = useMemo(() => ['pdf', 'doc', 'docx', 'xlsx', 'xls', 'txt', 'csv'], []);

  // ─── Form sections ─────────────────────────────────────────────────────

  const formSections: BaseFormSectionConfig[] = useMemo(() => {
    const isTemplateSelected = !!selectedTemplateId;

    return [
      {
        dataTourId: 'create-table-basics',
        fields: [
          {
            name: 'tableName',
            type: 'input',
            label: t('Table Name'),
            placeholder: t('e.g., Due Diligence Review — Project Alpha'),
            required: true,
            gridSize: 'full',
          } as BaseFormFieldConfig,
          {
            name: 'description',
            type: 'textarea',
            label: t('Description'),
            placeholder: t('Briefly describe what this table analyzes...'),
            rows: 2,
            gridSize: 'full',
          } as BaseFormFieldConfig,
          {
            name: 'collaborators',
            type: 'collaboratorSelector',
            label: t('Share with team members'),
            gridSize: 'full',
          } as BaseFormFieldConfig,
          ...(projectOptions.length > 0 ? [{
            name: 'project_uuid',
            type: 'dropdown' as const,
            label: t('Link to Project'),
            placeholder: t('Select a project'),
            required: false,
            customProps: {fullWidth: true} as any,
            gridSize: 'full' as const,
            options: projectOptions,
          } as BaseFormFieldConfig] : []),
        ],
      },
      {
        dataTourId: 'create-table-columns',
        title: t('Columns'),
        description: t('Select a template or define custom columns to query each document.'),
        fields: [
          {
            name: 'template_id',
            type: 'custom',
            label: t('Template'),
            gridSize: 'full',
            customComponent: TemplateSelector,
            customProps: {
              templates,
              selectedTemplate,
              onTemplateChange: handleTemplateChange,
            },
          } as BaseFormFieldConfig,
          {
            name: 'columns',
            type: 'custom',
            label: '',
            gridSize: 'full',
            customComponent: ColumnsEditor,
            customProps: {
              readOnly: false,
              templateName: isTemplateSelected ? selectedTemplate?.name : undefined,
              templateColumns: isTemplateSelected ? templateColumns : undefined,
            },
          } as BaseFormFieldConfig,
        ],
      },
      {
        dataTourId: 'create-table-documents',
        title: t('Documents'),
        description: t('Upload the documents you want to analyze. Supported: PDF, Word, Excel, TXT, CSV.'),
        fields: [
          {
            name: 'files',
            type: 'fileUploader',
            label: t('Files'),
            required: true,
            gridSize: 'full',
            allowedExtensions: tableFileExtensions,
            maxFileSize: 104857600, // 100MB per file
          } as BaseFormFieldConfig,
        ],
      },
    ];
  }, [t, templates, selectedTemplateId, selectedTemplate, templateColumns, tableFileExtensions, handleTemplateChange, projectOptions]);

  // ─── Validation ─────────────────────────────────────────────────────────

  const schema = useMemo(() => {
    return new FormSchema('createTable', [
      new FormField('tableName', 'text', true, t('Table Name')),
      new FormField('description', 'text', false, t('Description')),
      new FormField('template_id', 'text', false, t('Template')),
      new FormField('columns', 'text', true, t('At least one column'), {
        customValidator: (value: any) => {
          if (!value || !Array.isArray(value) || value.length === 0) {
            return { isValid: false, error: t('Define at least one column or select a template') };
          }
          for (let i = 0; i < value.length; i++) {
            if (!value[i].name?.trim()) {
              return { isValid: false, error: t('Column {{n}} needs a name', { n: i + 1 }) };
            }
            if (!value[i].query?.trim()) {
              return { isValid: false, error: t('Column {{n}} needs a query', { n: i + 1 }) };
            }
          }
          return { isValid: true };
        },
      }),
      new FormField('files', 'text', true, t('At least one document'), {
        customValidator: (value: any) => {
          if (!value || !Array.isArray(value) || value.length === 0) {
            return { isValid: false, error: t('Upload at least one document') };
          }
          return { isValid: true };
        },
      }),
      new FormField('collaborators', 'text', false, t('Access')),
    ]);
  }, [t]);

  // ─── Submit ─────────────────────────────────────────────────────────────

  const handleFormSubmit = useCallback(
    async (formData: Record<string, any>) => {
      setIsCreating(true);
      sseStartedAtRef.current = Date.now();

      const files: File[] = formData.files || [];

      // Always use formData.columns — template columns are seeded into the form when selected
      const columns = (formData.columns || []).map((c: ColumnItem, i: number) => ({
        name: c.name.trim(),
        query: c.query.trim(),
        data_type: c.data_type,
        sort_order: i,
      }));

      const payload: CreateTablePayload = {
        name: String(formData.tableName).trim(),
        description: formData.description?.trim() || undefined,
        ...(matterId ? { matter_id: matterId, matter_uuid: matterUuid } : {}),
        ...(selectedTemplateId ? { template_id: Number(selectedTemplateId) } : {}),
        ...(formData.project_uuid ? { project_uuid: formData.project_uuid } : {}),
        columns,
        shared_with: (formData.collaborators || []).map((c: Collaborator) => ({
          id: c.user_id,
          collaborator_uuid: c.collaborator_uuid,
          access: c.access || 'view',
        })),
      };

      try {
        const result = await dispatch(createBatchTableSSE(payload, files) as any);
        // SSE completed — navigate to the new table
        if (!userNavigatedAwayRef.current) {
          const tableUuid = result?.table?.uuid;
          dispatch(resetBatchTableCreation() as any);
          onClose(tableUuid);
        }
      } catch (error: any) {
        console.error('[CreateTableForm] SSE error:', error);
        // Even on error, if we have a table UUID, navigate to it
        if (error?.table?.uuid && !userNavigatedAwayRef.current) {
          setTimeout(() => {
            dispatch(resetBatchTableCreation() as any);
            onClose(error.table.uuid);
          }, 2000);
        } else {
          setIsCreating(false);
        }
      }
    },
    [dispatch, matterId, matterUuid, onClose, selectedTemplateId, templateColumns]
  );

  // ─── Initial values ────────────────────────────────────────────────────

  const initialValues = useMemo(
    () => ({
      tableName: '',
      description: '',
      template_id: '',
      columns: [] as ColumnItem[],
      files: [] as File[],
      collaborators: [] as Collaborator[],
      project_uuid: '',
    }),
    []
  );

  // ─── Cancel SSE ──────────────────────────────────────────────────────────

  const handleCancelCreation = useCallback(() => {
    userNavigatedAwayRef.current = true;
    dispatch(resetBatchTableCreation() as any);
    setIsCreating(false);
    onClose();
  }, [dispatch, onClose]);

  // ─── Render ─────────────────────────────────────────────────────────────

  if (isLoadingData) {
    return <FormSkeleton />;
  }

  // Show SSE progress view during creation (like StructureForm / CreateMatter)
  if (isCreating || isConnecting) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <SSECreationProgress
          title={t('Creating Table')}
          isConnecting={isConnecting}
          steps={sseSteps}
          connected={connected}
          complete={complete}
          ok={ok}
          summary={summary ?? undefined}
          startedAt={sseStartedAtRef.current ?? undefined}
          onCancel={handleCancelCreation}
          type="table"
        />
      </div>
    );
  }

  return (
    <>
      <BaseForm
        title={t('New Table')}
        description={t('Create a table to analyze batches of documents with AI-powered queries.')}
        sections={formSections}
        schema={schema}
        initialValues={initialValues}
        onSubmit={handleFormSubmit}
        onCancel={() => onClose()}
        submitLabel={t('Create Table')}
        cancelLabel={t('Cancel')}
        isSubmitting={isCreating}
        maxWidth="max-w-4xl"
      />
      <FeatureTour
        steps={createTableFormTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlay
      />
    </>
  );
};

export default CreateTableForm;
